import { Link } from "@react-navigation/native";
import {
    Alert,
    DataTable,
    Headline,
    Loading,
    SearchInput,
    Spacer,
    StyleFunction,
    Surface,
    usePagination,
    useThemedStyle,
} from "@venuepos/react-common";
import { useTagsByCustomerLazyQuery } from "graphql-sdk";
import { ICustomer } from "lib";
import { useTranslation } from "locales";
import React, { useCallback, useEffect, useState } from "react";

export function CustomerTags({ customer }: { customer: ICustomer["id"] }) {
    const styles = useThemedStyle(styleFunc);
    const [t] = useTranslation();
    const { page, pageSize, sortBy, sortDirection, onPageChange } =
        usePagination({
            initialPageSize: 10,
            initialSortBy: "identifier",
            initialSortDirection: "ASC",
        });

    const [getTagsByCustomer, { error, loading, data }] =
        useTagsByCustomerLazyQuery();

    const [search, setSearch] = useState<string>("");
    const handleSearchTextChange = useCallback(
        text => {
            setSearch(text);
            // return to first page in list
            onPageChange(0);
        },
        [onPageChange]
    );

    useEffect(() => {
        if (!customer) {
            return;
        }

        getTagsByCustomer({
            variables: {
                customerId: customer,
                pagination: {
                    page,
                    pageSize,
                    sort: sortBy,
                    sortDirection: sortDirection,
                },
                search: {
                    query: search,
                },
            },
        });
    }, [
        getTagsByCustomer,
        customer,
        page,
        pageSize,
        search,
        sortBy,
        sortDirection,
    ]);

    return (
        <Surface>
            <Headline size="h5">
                {t("backoffice.tags.related", "Related tags")}
            </Headline>
            <SearchInput
                onChange={handleSearchTextChange}
                testID="tagSearch"
                onClear={() => setSearch("")}
            />
            <DataTable>
                <DataTable.Header>
                    <DataTable.Title>
                        {t("common.name", "Name")}
                    </DataTable.Title>
                </DataTable.Header>
                {error ? (
                    <Alert type="error">
                        {t(
                            "backoffice.error.from_server",
                            "There was an error: {{errorText}}",
                            {
                                errorText: error.message,
                            }
                        )}
                    </Alert>
                ) : loading ? (
                    <>
                        <Spacer />
                        <Loading />
                        <Spacer />
                    </>
                ) : (
                    data?.tagsByCustomer.data.map((item, index) => (
                        <DataTable.Row
                            style={index % 2 === 0 ? styles.oddRow : undefined}
                            key={item.id}
                        >
                            <DataTable.Cell>
                                <Link
                                    to={{
                                        screen: "TAG_EDIT",
                                        params: { id: item.id },
                                    }}
                                    style={styles.textLink}
                                >
                                    {item.name || item.identifier}
                                </Link>
                            </DataTable.Cell>
                        </DataTable.Row>
                    ))
                )}
                <DataTable.Pagination
                    onPageChange={onPageChange}
                    pageSize={pageSize}
                    page={page}
                    numberOfPages={data?.tagsByCustomer.pagination.pages}
                    itemCount={data?.tagsByCustomer.pagination.resultCount}
                    disablePageSizeSelector
                />
            </DataTable>
        </Surface>
    );
}

const styleFunc: StyleFunction = theme => ({
    oddRow: {
        backgroundColor: theme.colors.grey50,
    },
    textLink: {
        ...theme.styles.link,
    },
});
